import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Search, Link2, Unlink } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { RevenueTrend, CampaignLinks, EstimatedMatches } from "./RevenueInsights";

const money = (paise, cur) => `${cur} ${(paise / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const errMsg = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : "Request failed"; };

function CurrencyBlock({ cur, b }) {
  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3" data-testid={`rev-currency-${cur}`}>
      <div className="text-[10px] font-bold uppercase text-emerald-700">Verified · {cur}</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1 text-sm">
        <div><div className="text-[10px] text-stone-500">Orders</div><b data-testid={`rev-orders-${cur}`}>{b.verified_orders}</b></div>
        <div><div className="text-[10px] text-stone-500">Gross</div><b data-testid={`rev-gross-${cur}`}>{money(b.gross_paise, cur)}</b></div>
        <div><div className="text-[10px] text-stone-500">Refunds</div><b data-testid={`rev-refunds-${cur}`}>{money(b.refunds_paise, cur)}</b></div>
        <div><div className="text-[10px] text-stone-500">Net</div><b data-testid={`rev-net-${cur}`}>{money(b.net_paise, cur)}</b></div>
      </div>
      {(b.refund_pending_orders > 0 || b.cancelled_after_payment_unrefunded > 0) && (
        <p className="text-[11px] text-amber-700 mt-1">{b.refund_pending_orders} refund pending (not deducted) · {b.cancelled_after_payment_unrefunded} cancelled after payment, no refund recorded</p>
      )}
    </div>
  );
}

function LinkOrder({ bizId, onLinked }) {
  const [q, setQ] = useState("");
  const [orders, setOrders] = useState(null);
  const [camps, setCamps] = useState([]);
  const [pick, setPick] = useState({});
  useEffect(() => { api.get(`/aina/campaigns?business_id=${bizId}`).then((r) => setCamps(r.data.items || r.data.campaigns || [])).catch(() => {}); }, [bizId]);
  const search = async () => {
    try { setOrders((await api.get(`/aina/automation/orders/search?business_id=${bizId}&q=${encodeURIComponent(q)}`)).data.items); } catch (e) { toast.error(errMsg(e)); }
  };
  const link = async (oid) => {
    try {
      await api.post("/aina/automation/attributions", { business_id: bizId, order_id: oid, campaign_id: pick[oid] || null, note: "manual owner link" });
      toast.success("Order linked"); search(); onLinked();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-xl border border-stone-200 p-3" data-testid="rev-link-panel">
      <div className="text-xs font-bold mb-2">Link an existing order to a campaign</div>
      <div className="flex gap-2">
        <input data-testid="rev-order-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Order ID (e.g. ord_…)" className="flex-1 px-3 py-1.5 rounded-lg border border-stone-200 text-sm" />
        <button data-testid="rev-order-search-btn" onClick={search} className="px-3 py-1.5 rounded-full bg-stone-900 text-white text-xs"><Search className="w-3 h-3 inline" /> Search</button>
      </div>
      {orders && (
        <ul className="mt-2 divide-y divide-stone-100" data-testid="rev-order-results">
          {orders.length === 0 && <li className="text-xs text-stone-500 py-2">No matching orders.</li>}
          {orders.map((o) => (
            <li key={o.order_id} className="py-2 flex flex-wrap items-center gap-2 text-xs" data-testid={`rev-order-${o.order_id}`}>
              <span className="font-mono">{o.order_id}</span>
              <span>{money(o.amount_paise, o.currency)}</span>
              <span className={o.payment_verified ? "text-emerald-700 font-bold" : "text-stone-500"}>{o.payment_verified ? "payment verified" : o.status}</span>
              {o.attribution ? <span className="text-indigo-700">linked · {o.attribution.method}</span> : (
                <span className="ml-auto flex gap-1">
                  <select data-testid={`rev-campaign-select-${o.order_id}`} className="border border-stone-200 rounded px-1" value={pick[o.order_id] || ""} onChange={(e) => setPick({ ...pick, [o.order_id]: e.target.value })}>
                    <option value="">Choose campaign</option>
                    {camps.map((c) => <option key={c.campaign_id} value={c.campaign_id}>{c.name}</option>)}
                  </select>
                  <button data-testid={`rev-link-btn-${o.order_id}`} disabled={!pick[o.order_id]} onClick={() => link(o.order_id)} className="px-2 py-1 rounded-full bg-indigo-600 text-white disabled:opacity-40"><Link2 className="w-3 h-3 inline" /> Link</button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function RevenuePanel({ bizId, tick }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rv, setRv] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    setError(null);
    const q = new URLSearchParams({ business_id: bizId, ...(from ? { from: new Date(`${from}T00:00:00`).toISOString() } : {}), ...(to ? { to: new Date(`${to}T23:59:59`).toISOString() } : {}) });
    try { setRv((await api.get(`/aina/automation/revenue?${q}`)).data); } catch (e) { setError(errMsg(e)); }
  }, [bizId, from, to]);
  useEffect(() => { load(); }, [load, tick]);
  const unlink = async (a) => {
    try { await api.post(`/aina/automation/attributions/${a.attribution_id}/remove`, { business_id: bizId, reason: "removed by owner from revenue panel" }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (error) return <p className="text-sm text-rose-700" data-testid="rev-error">{error}</p>;
  if (!rv) return <Loader2 className="w-4 h-4 animate-spin" />;
  const curs = Object.entries(rv.verified_by_currency);
  return (
    <div className="space-y-4" data-testid="revenue-panel">
      <div className="flex flex-wrap gap-2 items-center text-xs">
        <label>From <input data-testid="rev-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="border border-stone-200 rounded px-1" /></label>
        <label>To <input data-testid="rev-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="border border-stone-200 rounded px-1" /></label>
      </div>
      {curs.length ? curs.map(([c, b]) => <CurrencyBlock key={c} cur={c} b={b} />) : <p className="text-sm text-stone-500" data-testid="rev-empty">No verified attributed revenue yet.</p>}
      <div className="grid sm:grid-cols-2 gap-2 text-xs">
        <div className="rounded-xl bg-stone-50 p-3" data-testid="rev-pending"><b>Pending / unpaid attributed orders: {rv.pending_payment_attributed.count}</b>
          <div className="text-stone-500">{Object.entries(rv.pending_payment_attributed.by_status).map(([k, v]) => `${k}: ${v}`).join(" · ") || "none"} — excluded from revenue</div></div>
        <div className="rounded-xl bg-stone-50 p-3" data-testid="rev-unattributed"><b>Paid but unattributed orders: {rv.unattributed_verified.count}</b>
          <div className="text-stone-500">{Object.entries(rv.unattributed_verified.gross_paise).map(([c, p]) => money(p, c)).join(" · ") || "none"}</div></div>
      </div>
      {rv.by_campaign.length > 0 && (
        <table className="w-full text-xs" data-testid="rev-by-campaign">
          <thead><tr className="text-left text-stone-500"><th>Campaign</th><th>Currency</th><th>Orders</th><th>Net</th></tr></thead>
          <tbody>{rv.by_campaign.flatMap((g) => Object.entries(g.currencies).map(([c, b]) => (
            <tr key={g.campaign_id + c} className="border-t border-stone-100"><td>{g.campaign_name || g.campaign_id}</td><td>{c}</td><td>{b.verified_orders}</td><td>{money(b.net_paise, c)}</td></tr>
          )))}</tbody>
        </table>
      )}
      {rv.attributed_orders.length > 0 && (
        <ul className="text-xs divide-y divide-stone-100" data-testid="rev-attributions">
          {rv.attributed_orders.map((a) => (
            <li key={a.attribution_id} className="py-1.5 flex flex-wrap gap-2 items-center">
              <span className="font-mono">{a.order_id}</span><span>{a.method === "manual_owner_link" ? "manual link" : "checkout reference"}</span>
              <span className={a.payment_verified ? "text-emerald-700" : "text-stone-500"}>{a.payment_verified ? "verified" : `unpaid (${a.order_status})`}</span>
              <button data-testid={`rev-unlink-${a.attribution_id}`} onClick={() => unlink(a)} className="ml-auto text-stone-500"><Unlink className="w-3 h-3 inline" /> Remove</button>
            </li>
          ))}
        </ul>
      )}
      <RevenueTrend bizId={bizId} />
      <EstimatedMatches bizId={bizId} onChanged={load} />
      <LinkOrder bizId={bizId} onLinked={load} />
      <CampaignLinks bizId={bizId} />
      <details className="text-[11px] text-stone-600" data-testid="rev-policy">
        <summary className="cursor-pointer font-semibold">How verified revenue is calculated</summary>
        <ul className="list-disc pl-4 mt-1 space-y-0.5">{Object.entries(rv.policy).map(([k, v]) => <li key={k}><b>{k}:</b> {v}</li>)}</ul>
      </details>
    </div>
  );
}
